/**
 * Curated café selection for the public /work-cafes/:city landing pages.
 *
 * Pure and deterministic so the same cafés render at prerender time and at
 * runtime. Curation (pin / hide / blurb) comes from `seo_city_page_cafes`;
 * everything else is live café data.
 *
 * List pages run the rule in `cityPageRule.ts`: threshold, cap, fill.
 * Hub pages keep the earlier behaviour, every scored café with no cap.
 */

import {
  CITY_PAGE_LIST_MIN,
  CITY_PAGE_MAX_CAFES,
  CITY_PAGE_SCORE_THRESHOLD,
  type CityPageKind,
} from "./cityPageRule";

/**
 * Pool floor for sitemap and prerender eligibility, measured on the pool of
 * scored cafés in the area BEFORE the threshold, the cap and the fill. A
 * display rule must never remove a page from Google, which is why this is a
 * separate constant from CITY_PAGE_LIST_MIN.
 */
export const CITY_PAGE_MIN_CAFES = 8;


export interface CityPageCafeInput {
  id: string;
  name: string;
  work_readiness_score: number | null;
}

export interface CityPageCuration {
  cafe_id: string;
  blurb: string | null;
  pin_order: number | null;
  hidden: boolean;
}

export interface SelectedCityCafe<T extends CityPageCafeInput> {
  cafe: T;
  /** Null/empty blurbs stay null — the card renders no blurb rather than filler. */
  blurb: string | null;
  rank: number;
}

/** Score desc, then name asc, then id asc. Duplicate café names are real. */
function compareCafes<T extends CityPageCafeInput>(a: T, b: T): number {
  const scoreDiff = (b.work_readiness_score ?? 0) - (a.work_readiness_score ?? 0);
  if (scoreDiff !== 0) return scoreDiff;
  const nameDiff = a.name.localeCompare(b.name);
  if (nameDiff !== 0) return nameDiff;
  return a.id.localeCompare(b.id);
}

/**
 * 1. Drop hidden cafés, then order deterministically.
 * 2. List page: keep cafés at or above the threshold, capped. If fewer than
 *    CITY_PAGE_LIST_MIN reach it, fill from the next highest scoring cafés.
 *    A pinned café must also reach the threshold and occupies one slot.
 * 3. Hub page: every café, no cap, and below CITY_PAGE_MIN_CAFES the list is
 *    empty, exactly as before.
 * 4. Pins reorder the chosen set, they never add to it.
 */
export function selectCityCafes<T extends CityPageCafeInput>(
  cafes: T[],
  curations: CityPageCuration[],
  options: { pageKind?: CityPageKind } = {},
): SelectedCityCafe<T>[] {
  const pageKind = options.pageKind ?? "hub";
  const byCafeId = new Map<string, CityPageCuration>();
  for (const c of curations) byCafeId.set(c.cafe_id, c);

  const eligible = cafes.filter((c) => !byCafeId.get(c.id)?.hidden).sort(compareCafes);

  const pinRank = (c: T) => byCafeId.get(c.id)?.pin_order ?? null;
  const byPin = (a: T, b: T) => {
    const pa = pinRank(a)!;
    const pb = pinRank(b)!;
    if (pa !== pb) return pa - pb;
    return compareCafes(a, b);
  };

  let ordered: T[];
  if (pageKind === "list") {
    const qualified = eligible.filter(
      (c) => (c.work_readiness_score ?? 0) >= CITY_PAGE_SCORE_THRESHOLD,
    );

    // A pin controls order among qualifiers, never membership. A pinned café
    // below the threshold is never lifted by its pin; it appears only if it is
    // one of the next highest scoring fill cafés, and always after qualifiers.
    const pinned = qualified.filter((c) => pinRank(c) != null).sort(byPin);
    const pinnedIds = new Set(pinned.map((c) => c.id));
    const orderedQualifiers = [...pinned, ...qualified.filter((c) => !pinnedIds.has(c.id))];

    // Below the floor, the next highest scoring cafés fill the list out. Fill
    // cafés keep score order and are never reordered by a pin.
    const qualifiedIds = new Set(qualified.map((c) => c.id));
    const fill =
      orderedQualifiers.length >= CITY_PAGE_LIST_MIN
        ? []
        : eligible
            .filter((c) => !qualifiedIds.has(c.id))
            .slice(0, CITY_PAGE_LIST_MIN - orderedQualifiers.length);

    ordered = [...orderedQualifiers, ...fill].slice(0, CITY_PAGE_MAX_CAFES);
  } else {
    if (eligible.length < CITY_PAGE_MIN_CAFES) return [];
    const pinned = eligible.filter((c) => pinRank(c) != null).sort(byPin);
    const pinnedIds = new Set(pinned.map((c) => c.id));
    ordered = [...pinned, ...eligible.filter((c) => !pinnedIds.has(c.id))];
  }

  return ordered.map((cafe, i) => {
    const blurb = byCafeId.get(cafe.id)?.blurb;
    return {
      cafe,
      blurb: blurb && blurb.trim().length > 0 ? blurb.trim() : null,
      rank: i + 1,
    };
  });
}

/**
 * Sitemap and prerender eligibility. Measures the pool of scored cafés the
 * page's matching rule selects, never the displayed list, so a page showing 7
 * cards stays in the sitemap.
 */
export function isCityPagePoolEligible(poolSize: number): boolean {
  return poolSize >= CITY_PAGE_MIN_CAFES;
}

